import { Box, type BoxProps } from '@chakra-ui/react'
import Image from 'next/legacy/image'
import { useCallback } from 'react'

import logger from '@/utils/logger'
import { ShwEnv } from '@/constants/types'

import { getPublicUrl } from '@/client/services/url'
import { WEBSITE_ENV } from '@/client/settings'
import { cloudflareImageLoader, isImageSupportedByLoader } from '@/client/vendor/cloudflare'

import { breakpoints } from '@/components/UI/common.styled'

export default function OptimizedImage({
  src,
  originalWidth,
  originalHeight,
  displayWidth,
  alt,
  publicImage = false,
  sizes,
  ...props
}: {
  src: string
  originalWidth: number
  originalHeight: number
  displayWidth: number | string | (number | string)[]
  alt: string
  publicImage?: boolean
  sizes?: string
} & Omit<BoxProps, 'width'>): React.ReactElement {
  let generatedSizes = sizes != null ? sizes : ''

  if (sizes == null) {
    if (typeof displayWidth === 'number') {
      generatedSizes = `${displayWidth}px`
    } else if (typeof displayWidth === 'string') {
      generatedSizes = displayWidth
    } else if (Array.isArray(displayWidth)) {
      generatedSizes = displayWidth
        .map(width => (typeof width === 'number' ? `${width}px` : width))
        .map(
          (width, index) =>
            `${
              index !== displayWidth.length - 1 ? `(max-width: ${breakpoints[index]})` : ''
            } ${width}`
        )
        .join(', ')
    }
  }

  const imageSrc = publicImage ? `${getPublicUrl(src, { forceProduction: true })}` : src
  const onImageError = useCallback(() => {
    logger.error('CloudFlare image loader error', { src: imageSrc })
  }, [imageSrc])

  const loader =
    isImageSupportedByLoader(src) && WEBSITE_ENV !== ShwEnv.DEVELOPMENT
      ? cloudflareImageLoader
      : undefined

  return (
    <Box width={displayWidth} {...props}>
      <Image
        loader={loader}
        src={imageSrc}
        width={originalWidth}
        height={originalHeight}
        alt={alt}
        sizes={generatedSizes}
        layout="responsive"
        onError={onImageError}
      />
    </Box>
  )
}
